import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Minus, Plus, Check, MapPin } from "lucide-react";
import { FLAVORS, KIT5_PRICE, PRICE_RECHEADO, PRICE_TRADICIONAL, WHATSAPP_NUMBER } from "@/lib/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtBRL = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const priceFor = (value) => FLAVORS.find((f) => f.value === value)?.price ?? PRICE_RECHEADO;

function formatPhone(v) {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function formatCep(v) {
  const d = v.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 5) return d;
  return `${d.slice(0, 5)}-${d.slice(5)}`;
}

const buildWaText = ({ name, type, breakdown, kitQty, total, notes, address }) => {
  let body = `Olá! Sou ${name}. Quero fechar meu pedido na *The Quick Brownie*.\n\n`;
  if (type === "unit") {
    body += `*Avulso · escolha de sabores:*\n`;
    Object.entries(breakdown).filter(([, q]) => q > 0).forEach(([f, q]) => (body += `· ${q}× ${f}\n`));
  } else {
    body += `*Kit · 1 de cada sabor* · ${kitQty}× kit(s)\n`;
    Object.entries(breakdown).filter(([, q]) => q > 0).forEach(([f, q]) => (body += `· ${q * kitQty}× ${f}\n`));
  }
  body += `\n*Total:* ${fmtBRL(total)}\n`;
  if (address?.street) {
    body += `\n*Endereço de entrega:*\n`;
    body += `${address.street}, ${address.number || "s/n"}`;
    if (address.complement) body += ` · ${address.complement}`;
    body += `\n${address.neighborhood ? address.neighborhood + " · " : ""}${address.city}/${address.state}`;
    if (address.cep) body += `\nCEP: ${address.cep}`;
  }
  if (notes) body += `\n*Obs:* ${notes}\n`;
  body += `\nSem glúten · Sem lactose · Com colágeno hidrolisado.`;
  return body;
};

export default function OrderForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [orderType, setOrderType] = useState("unit");

  const initialUnit = useMemo(() => Object.fromEntries(FLAVORS.map((f) => [f.value, 0])), []);
  const [unitBreakdown, setUnitBreakdown] = useState(initialUnit);
  const kitBreakdown = useMemo(() => Object.fromEntries(FLAVORS.map((f) => [f.value, 1])), []);
  const [kitQty, setKitQty] = useState(1);

  const [cep, setCep] = useState("");
  const [street, setStreet] = useState("");
  const [number, setNumber] = useState("");
  const [complement, setComplement] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [city, setCity] = useState("");
  const [stateUF, setStateUF] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  const [cepError, setCepError] = useState("");

  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const chosen = FLAVORS.find((f) => f.value === e.detail);
      if (chosen) {
        setOrderType("unit");
        setUnitBreakdown((prev) => ({ ...prev, [chosen.value]: (prev[chosen.value] || 0) + 1 }));
      }
    };
    window.addEventListener("tqb:select-flavor", handler);
    return () => window.removeEventListener("tqb:select-flavor", handler);
  }, []);

  const unitQtyTotal = Object.values(unitBreakdown).reduce((a, b) => a + b, 0);
  const unitSubtotal = Object.entries(unitBreakdown).reduce(
    (sum, [val, q]) => sum + q * priceFor(val),
    0
  );
  const total = orderType === "unit" ? unitSubtotal : kitQty * KIT5_PRICE;

  const adjustUnit = (val, delta) => {
    setUnitBreakdown((prev) => ({
      ...prev,
      [val]: Math.max(0, Math.min(200, (prev[val] || 0) + delta)),
    }));
  };

  // ViaCEP lookup
  const lookupCep = async (rawCep) => {
    const digits = rawCep.replace(/\D/g, "");
    if (digits.length !== 8) return;
    setCepLoading(true);
    setCepError("");
    try {
      const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await res.json();
      if (data?.erro) {
        setCepError("CEP não encontrado.");
        return;
      }
      setStreet(data.logradouro || "");
      setNeighborhood(data.bairro || "");
      setCity(data.localidade || "");
      setStateUF(data.uf || "");
      setTimeout(() => document.getElementById("addr-number")?.focus(), 50);
    } catch {
      setCepError("Falha ao buscar CEP.");
    } finally {
      setCepLoading(false);
    }
  };

  const onCepChange = (e) => {
    const v = formatCep(e.target.value);
    setCep(v);
    if (v.replace(/\D/g, "").length === 8) lookupCep(v);
    else setCepError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || name.trim().length < 2) return toast.error("Informe seu nome.");
    if (phone.replace(/\D/g, "").length < 10) return toast.error("Informe um WhatsApp válido.");
    if (orderType === "unit" && unitQtyTotal < 1) return toast.error("Adicione pelo menos 1 brownie.");
    if (cep && cep.replace(/\D/g, "").length !== 8) return toast.error("CEP inválido.");
    if (cep && !number.trim()) return toast.error("Informe o número do endereço.");

    const breakdown = orderType === "unit" ? unitBreakdown : kitBreakdown;
    const payload = {
      name: name.trim(),
      phone,
      order_type: orderType,
      kit_breakdown: Object.fromEntries(Object.entries(breakdown).filter(([, q]) => q > 0)),
      kit_quantity: orderType === "kit5" ? kitQty : 1,
      notes: notes.trim() || null,
      cep: cep || null,
      street: street.trim() || null,
      address_number: number.trim() || null,
      complement: complement.trim() || null,
      neighborhood: neighborhood.trim() || null,
      city: city.trim() || null,
      state: stateUF.trim() || null,
    };

    setLoading(true);
    try {
      await axios.post(`${API}/orders`, payload);
      const wa = encodeURIComponent(
        buildWaText({
          name: name.trim(), type: orderType, breakdown, kitQty, total, notes: notes.trim(),
          address: { cep, street, number, complement, neighborhood, city, state: stateUF },
        })
      );
      setSuccess(true);
      toast.success("Pedido recebido. Abrindo WhatsApp...");
      setTimeout(() => window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${wa}`, "_blank", "noopener"), 600);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Não foi possível enviar.";
      toast.error(typeof msg === "string" ? msg : "Falha ao enviar pedido.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSuccess(false);
    setName(""); setPhone(""); setUnitBreakdown(initialUnit); setKitQty(1);
    setCep(""); setStreet(""); setNumber(""); setComplement("");
    setNeighborhood(""); setCity(""); setStateUF(""); setCepError("");
    setNotes("");
  };

  return (
    <section id="pedido" data-testid="order-section" className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <div className="grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em]">
            <span className="inline-block w-10 h-px bg-black" />
            <span>Pedido rápido</span>
          </div>
          <h2 className="font-display mt-6 text-4xl sm:text-5xl md:text-7xl leading-[0.95] tracking-[-0.02em]">
            Peça o seu.<br />Sem atalhos.
          </h2>
          <p className="mt-8 text-base leading-relaxed max-w-md">
            Preencha os dados e o endereço de entrega. Confirmação e pagamento são
            finalizados pelo WhatsApp — abrimos a conversa pra você automaticamente.
          </p>

          <div className="hairline mt-12" />
          <ul className="mt-8 space-y-4 text-sm">
            <li className="flex items-start gap-3"><Check size={18} className="mt-0.5" /> Sem açúcar · sem glúten · sem lactose · com colágeno hidrolisado.</li>
            <li className="flex items-start gap-3"><Check size={18} className="mt-0.5" /> Preparo sob encomenda · entrega em até 5 dias úteis.</li>
            <li className="flex items-start gap-3"><Check size={18} className="mt-0.5" /> Pagamento via Pix na confirmação.</li>
          </ul>
        </div>

        <form data-testid="order-form" onSubmit={submit} className="md:col-span-7 border border-black p-6 md:p-10 bg-white">
          {success ? (
            <div data-testid="order-success" className="py-10 text-center">
              <h3 className="font-display text-4xl md:text-5xl tracking-[-0.02em]">Pedido recebido.</h3>
              <p className="mt-4 text-base leading-relaxed max-w-md mx-auto">
                Estamos abrindo seu WhatsApp com a mensagem pré-preenchida (incluindo seu
                endereço). Se não abrir automaticamente, clique no botão abaixo.
              </p>
              <div className="flex flex-wrap gap-3 justify-center mt-8">
                <button type="button" onClick={reset} className="btn-outline" data-testid="order-new">Fazer novo pedido</button>
              </div>
            </div>
          ) : (
            <div className="space-y-10">
              {/* TYPE TOGGLE */}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] mb-3">1 · Tipo de compra</p>
                <div className="grid grid-cols-2 gap-0 border border-black">
                  {[
                    { v: "unit", label: "Avulso · escolha sabores", price: "R$ 19,90 trad. · R$ 23,90 recheado" },
                    { v: "kit5", label: "Kit · 1 de cada", price: "R$ 97,90 · com desconto" },
                  ].map((opt, i) => {
                    const sel = orderType === opt.v;
                    return (
                      <button key={opt.v} type="button" data-testid={`order-type-${opt.v}`} onClick={() => setOrderType(opt.v)}
                        className={`py-4 px-4 text-left transition-colors ${sel ? "bg-black text-white" : "bg-white text-black hover:bg-black hover:text-white"} ${i === 0 ? "border-r border-black" : ""}`}>
                        <span className="font-display text-lg block">{opt.label}</span>
                        <span className="text-xs uppercase tracking-[0.18em] opacity-80">{opt.price}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* SELECTION */}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] mb-3">2 · {orderType === "unit" ? "Sabores e quantidades" : "Quantos kits?"}</p>
                {orderType === "unit" ? (
                  <>
                    <ul className="border border-black">
                      {FLAVORS.map((f, i) => (
                        <li key={f.slug} className={`flex items-center justify-between gap-4 px-4 py-3 ${i < FLAVORS.length - 1 ? "border-b border-black" : ""}`}>
                          <div className="min-w-0">
                            <span className="font-display text-base md:text-lg block">{f.label}</span>
                            <span className="text-[11px] uppercase tracking-[0.18em]">{fmtBRL(f.price)} · unidade</span>
                          </div>
                          <div className="flex items-center border border-black shrink-0">
                            <button type="button" data-testid={`unit-minus-${f.slug}`} onClick={() => adjustUnit(f.value, -1)} className="qty-btn border-r border-black" aria-label={`Remover ${f.label}`}><Minus size={14} className="mx-auto" /></button>
                            <div data-testid={`unit-qty-${f.slug}`} className="w-10 h-10 flex items-center justify-center font-display">{unitBreakdown[f.value]}</div>
                            <button type="button" data-testid={`unit-plus-${f.slug}`} onClick={() => adjustUnit(f.value, +1)} className="qty-btn border-l border-black" aria-label={`Adicionar ${f.label}`}><Plus size={14} className="mx-auto" /></button>
                          </div>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 text-xs uppercase tracking-[0.2em]" data-testid="unit-counter">{unitQtyTotal} {unitQtyTotal === 1 ? "selecionado" : "selecionados"}</p>
                  </>
                ) : (
                  <>
                    <ul data-testid="kit-fixed-list" className="border border-black">
                      {FLAVORS.map((f, i) => (
                        <li key={f.slug} className={`flex items-center justify-between gap-4 px-4 py-3 ${i < FLAVORS.length - 1 ? "border-b border-black" : ""}`}>
                          <span className="font-display text-base md:text-lg">{f.label}</span>
                          <span className="font-display text-sm">{kitQty}×</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-between mt-5">
                      <span className="text-xs uppercase tracking-[0.2em]">Quantidade de kits</span>
                      <div className="flex items-center border border-black">
                        <button type="button" data-testid="kit-qty-minus" onClick={() => setKitQty((q) => Math.max(1, q - 1))} className="qty-btn border-r border-black" aria-label="Menos um kit"><Minus size={14} className="mx-auto" /></button>
                        <div data-testid="kit-qty-value" className="w-12 h-10 flex items-center justify-center font-display text-lg">{kitQty}</div>
                        <button type="button" data-testid="kit-qty-plus" onClick={() => setKitQty((q) => Math.min(50, q + 1))} className="qty-btn border-l border-black" aria-label="Mais um kit"><Plus size={14} className="mx-auto" /></button>
                      </div>
                    </div>
                    <p className="mt-3 text-sm">
                      Você economiza <span className="font-medium">{fmtBRL((4 * PRICE_RECHEADO + PRICE_TRADICIONAL - KIT5_PRICE) * kitQty)}</span> em relação à compra avulsa.
                    </p>
                  </>
                )}
              </div>

              {/* CUSTOMER */}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] mb-3">3 · Seus dados</p>
                <div className="grid sm:grid-cols-2 gap-x-6 gap-y-6">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="order-name-input">Nome completo</label>
                    <input id="order-name-input" data-testid="order-name" className="input-clean mt-2" placeholder="Como devemos te chamar?" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="order-phone-input">WhatsApp</label>
                    <input id="order-phone-input" data-testid="order-phone" className="input-clean mt-2" placeholder="(11) 99999-9999" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} inputMode="numeric" required />
                  </div>
                </div>
              </div>

              {/* ADDRESS */}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] mb-3 flex items-center gap-2"><MapPin size={14} /> 4 · Endereço de entrega</p>
                <div className="grid sm:grid-cols-12 gap-x-6 gap-y-6">
                  <div className="sm:col-span-4">
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="addr-cep">CEP</label>
                    <div className="relative">
                      <input id="addr-cep" data-testid="addr-cep" className="input-clean mt-2" placeholder="00000-000" value={cep} onChange={onCepChange} inputMode="numeric" maxLength={9} />
                      {cepLoading && (
                        <span className="absolute right-0 top-3 text-[10px] uppercase tracking-[0.2em] animate-pulse">Buscando…</span>
                      )}
                    </div>
                    {cepError && <p data-testid="cep-error" className="text-xs mt-1">{cepError}</p>}
                    <a href="https://buscacepinter.correios.com.br/app/endereco/index.php" target="_blank" rel="noreferrer" className="text-[11px] uppercase tracking-[0.18em] underline underline-offset-4 mt-2 inline-block">Não sei meu CEP</a>
                  </div>
                  <div className="sm:col-span-8">
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="addr-street">Rua / Logradouro</label>
                    <input id="addr-street" data-testid="addr-street" className="input-clean mt-2" placeholder="Preenchido pelo CEP" value={street} onChange={(e) => setStreet(e.target.value)} maxLength={200} />
                  </div>

                  <div className="sm:col-span-3">
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="addr-number">Número</label>
                    <input id="addr-number" data-testid="addr-number" className="input-clean mt-2" placeholder="123" value={number} onChange={(e) => setNumber(e.target.value)} maxLength={20} />
                  </div>
                  <div className="sm:col-span-5">
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="addr-complement">Complemento (opcional)</label>
                    <input id="addr-complement" data-testid="addr-complement" className="input-clean mt-2" placeholder="Apto, bloco, referência" value={complement} onChange={(e) => setComplement(e.target.value)} maxLength={120} />
                  </div>
                  <div className="sm:col-span-4">
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="addr-neighborhood">Bairro</label>
                    <input id="addr-neighborhood" data-testid="addr-neighborhood" className="input-clean mt-2" placeholder="Preenchido pelo CEP" value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} maxLength={120} />
                  </div>

                  <div className="sm:col-span-9">
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="addr-city">Cidade</label>
                    <input id="addr-city" data-testid="addr-city" className="input-clean mt-2" placeholder="Preenchido pelo CEP" value={city} onChange={(e) => setCity(e.target.value)} maxLength={120} />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="text-xs uppercase tracking-[0.2em]" htmlFor="addr-state">UF</label>
                    <input id="addr-state" data-testid="addr-state" className="input-clean mt-2 uppercase" placeholder="UF" value={stateUF} onChange={(e) => setStateUF(e.target.value.toUpperCase().slice(0, 2))} maxLength={2} />
                  </div>
                </div>
              </div>

              {/* NOTES + TOTAL */}
              <div>
                <label className="text-xs uppercase tracking-[0.2em]" htmlFor="order-notes-input">Observações (opcional)</label>
                <input id="order-notes-input" data-testid="order-notes" className="input-clean mt-2" placeholder="Horário preferido, presente, recado..." value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={500} />
              </div>

              <div className="hairline" />
              <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em]">Total</p>
                  <p data-testid="order-total" className="font-display text-4xl md:text-5xl mt-1 tracking-[-0.02em]">{fmtBRL(total)}</p>
                </div>
                <button type="submit" data-testid="order-submit" disabled={loading} className="btn-primary disabled:opacity-60">
                  {loading ? "Enviando..." : "Peça agora"}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
